import { assertEvent, type DoneActorEvent } from 'xstate5';
import { type GuardArgs } from 'xstate5/dist/declarations/src/guards';

import { type FetchWantedTimesResponse } from './ordering-machine.actors';
import { type OrderingContext } from './ordering-machine.context';
import { type OrderingEvent } from './ordering-machine.events';
import { orderingMachineLog } from './ordering-machine.logger';
import { isWantedTimeToday } from './ordering-machine.utils';

// ─────────────────────────────────────────────────────────────────────────────

const checkIfLoggedIn: Guard = () => {
  orderingMachineLog.warn('Please provide `checkIfLoggedIn` guard.');
  return false;
};

const checkIfShouldFetchInitialPaymentState: Guard = () => {
  orderingMachineLog.warn('Please provide `checkIfShouldFetchInitialPaymentState` guard.');
  return false;
};

const checkIfHasWantedTimes: Guard = ({ event }) => {
  const doneEvent = event as DoneActorEvent<FetchWantedTimesResponse>;
  const wantedTimes = doneEvent.output?.wantedTimes;

  return Array.isArray(wantedTimes) && wantedTimes.length > 0;
};

const checkIfDoesNotNeedsToShowFutureDateModal: Guard = ({ context, event }) => {
  assertEvent(event, 'CHANGE_TIME');
  return !event.params.isFutureDate || context.hasShownFutureDateWarning;
};

const checkIfNeedsToShowFutureDateModal: Guard = ({ context, event }) => {
  assertEvent(event, 'CHANGE_TIME');
  return event.params.isFutureDate && !context.hasShownFutureDateWarning;
};

const checkIfOrderSubmissionSuccess: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.submitOrder');
  return Boolean(event.output?.success);
};

const checkIfTimeslotIsUnavailable: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.submitOrder');
  return Boolean(!event.output?.success && event.output?.isTimeslotUnavailable);
};

const checkIfWillBeFutureOrder: Guard = ({ context, event }) => {
  assertEvent(event, 'xstate.done.actor.submitOrder');

  if (event.output?.success || !event.output?.isTimeslotUnavailable) {
    return false;
  }

  const nextTimeslot = context.cart.wantedTimes.find(
    (wantedTime) => wantedTime !== context.wantedTime,
  );

  return !isWantedTimeToday(nextTimeslot);
};

const checkIfGenericOrderSubmissionError: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.submitOrder');
  return Boolean(!event.output?.success && !event.output?.isTimeslotUnavailable);
};

const checkIfGiftCardApplied: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.applyGiftCard');
  const balance = (event.output as { giftCardBalance?: number } | null)?.giftCardBalance;
  return Boolean(balance);
};

const checkIfGiftCardNotApplied: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.applyGiftCard');
  const balance = (event.output as { giftCardBalance?: number } | null)?.giftCardBalance;
  return !balance;
};

const checkIfRewardApplied: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.applyReward');
  return Boolean('cart' in event.output);
};

const checkIfRewardNotApplied: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.applyReward');
  return Boolean('error' in event.output);
};

const checkIfRewardRemoved: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.removeReward');
  return Boolean('cart' in event.output);
};

const checkIfRewardNotRemoved: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.removeReward');
  return Boolean('error' in event.output);
};

const checkIfPromoCodeApplied: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.applyPromoCode');
  return Boolean(event.output?.success);
};

// ─────────────────────────────────────────────────────────────────────────────

export const OrderingGuards = {
  checkIfLoggedIn,
  checkIfHasWantedTimes,
  checkIfDoesNotNeedsToShowFutureDateModal,
  checkIfNeedsToShowFutureDateModal,
  checkIfOrderSubmissionSuccess,
  checkIfTimeslotIsUnavailable,
  checkIfWillBeFutureOrder,
  checkIfGenericOrderSubmissionError,
  checkIfGiftCardApplied,
  checkIfGiftCardNotApplied,
  checkIfRewardApplied,
  checkIfRewardNotApplied,
  checkIfRewardRemoved,
  checkIfRewardNotRemoved,
  checkIfPromoCodeApplied,
  checkIfShouldFetchInitialPaymentState,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Guard = (args: GuardArgs<OrderingContext, OrderingEvent>) => boolean;
